import React, { Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SafeNavigate } from "@/components/auth/SafeNavigate";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getDashboardRouteForRole } from "@/lib/access";
import { captureSentryException } from "@/lib/sentry";
import NotFound from "./pages/NotFound";

// Lazy import with retry logic for chunk load errors
function lazyWithRetry(importFunc: () => Promise<any>) {
  return React.lazy(async () => {
    const MAX_RETRIES = 3;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await importFunc();
      } catch (error) {
        // Check if it's a chunk load error
        const isChunkLoadError =
          error instanceof Error &&
          (error.message.includes('dynamically imported module') ||
           error.message.includes('Failed to fetch') ||
           error.name === 'ChunkLoadError');

        if (isChunkLoadError && attempt < MAX_RETRIES) {
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }

        // If it's the last attempt or not a chunk error, reload the page
        if (isChunkLoadError && attempt === MAX_RETRIES) {
          console.error('Chunk load failed after retries, reloading page...');
          window.location.reload();
        }

        throw error;
      }
    }

    // This should never be reached, but TypeScript needs it
    throw new Error('Failed to load module');
  });
}

// Lazy-loaded pages
const LandingPage = lazyWithRetry(() => import("./pages/LandingPage"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const LawyerDashboard = lazyWithRetry(() => import("./pages/LawyerDashboard"));
const DealIntakeReview = lazyWithRetry(() => import("./pages/DealIntakeReview"));
const MatterDetail = lazyWithRetry(() => import("./pages/MatterDetail"));
const AgentDashboard = lazyWithRetry(() => import("./pages/AgentDashboard"));
const DealIntakeForm = lazyWithRetry(() => import("./pages/DealIntakeForm"));
const AgentDealStatus = lazyWithRetry(() => import("./pages/AgentDealStatus"));
const ClientDashboard = lazyWithRetry(() => import("./pages/ClientDashboard"));
const ClientOnboarding = lazyWithRetry(() => import("./pages/ClientOnboarding"));
const ClientTransactionDetail = lazyWithRetry(() => import("./pages/ClientTransactionDetail"));
const Properties = lazyWithRetry(() => import("./pages/Properties"));
const Transactions = lazyWithRetry(() => import("./pages/Transactions"));
const TransactionDetail = lazyWithRetry(() => import("./pages/TransactionDetail"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const MessageDirectory = lazyWithRetry(() => import("./pages/MessageDirectory"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const CalendarOAuthCallback = lazyWithRetry(() => import("./pages/CalendarOAuthCallback"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Payments = lazyWithRetry(() => import("./pages/Payments"));
const CameraCapture = lazyWithRetry(() => import("./pages/CameraCapture"));
const SuperAdminDashboard = lazyWithRetry(() => import("./pages/SuperAdminDashboard"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const LegalReview = lazyWithRetry(() => import("./pages/LegalReview"));
const DueDiligence = lazyWithRetry(() => import("./pages/DueDiligence"));
const Help = lazyWithRetry(() => import("./pages/Help"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const Pipeline = lazyWithRetry(() => import("./pages/Pipeline"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ErrorButton() {
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        throw new Error("This is your first error!");
      }}
      className="fixed bottom-4 right-4 z-50 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-red-700"
    >
      Break the world
    </button>
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureSentryException(error, {
        tags: {
          source: "react-query",
          queryKey: JSON.stringify(query.queryKey),
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      captureSentryException(error, {
        tags: {
          source: "react-mutation",
          mutationKey: JSON.stringify(mutation.options.mutationKey),
        },
      });
    },
  }),
});

function RoleRedirect() {
  const { profile, isLoading, user, isSuperAdmin } = useAuth();
  if (isLoading) return null;
  if (!user) return <LandingPage />;
  if (isSuperAdmin) return <SafeNavigate to="/superadmin-dashboard" replace />;
  return <SafeNavigate to={getDashboardRouteForRole(profile?.role)} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <ErrorButton />
      <AuthProvider>
        <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<RoleRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/calendar/oauth/callback" element={<CalendarOAuthCallback />} />

              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Lawyer routes */}
                          <Route path="/lawyer-dashboard" element={<ProtectedRoute allowedRoles={['lawyer']}><LawyerDashboard /></ProtectedRoute>} />
                          <Route path="/deal-intake/:intakeId" element={<ProtectedRoute allowedRoles={['lawyer']}><DealIntakeReview /></ProtectedRoute>} />
                          <Route path="/matters/:matterId" element={<ProtectedRoute allowedRoles={['lawyer']}><MatterDetail /></ProtectedRoute>} />
                          {/* Agent routes */}
                          <Route path="/agent-dashboard" element={<ProtectedRoute allowedRoles={['agent']}><AgentDashboard /></ProtectedRoute>} />
                          <Route path="/deal-intake" element={<ProtectedRoute allowedRoles={['agent']}><DealIntakeForm /></ProtectedRoute>} />
                          <Route path="/deal-status/:dealId" element={<ProtectedRoute allowedRoles={['agent']}><AgentDealStatus /></ProtectedRoute>} />
                          {/* Client routes */}
                          <Route path="/client-dashboard" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />
                          <Route path="/client-onboarding" element={<ProtectedRoute allowedRoles={['lawyer', 'agent', 'client']}><ClientOnboarding /></ProtectedRoute>} />
                          <Route path="/client-transaction/:transactionId" element={<ProtectedRoute allowedRoles={['client']}><ClientTransactionDetail /></ProtectedRoute>} />
                          <Route path="/superadmin-dashboard" element={<ProtectedRoute requireSuperAdmin><SuperAdminDashboard /></ProtectedRoute>} />
                          {/* Shared routes */}
                          <Route path="/transactions" element={<Transactions />} />
                          <Route path="/transactions/:transactionId" element={<TransactionDetail />} />
                          <Route path="/properties" element={<Properties />} />
                          <Route path="/messages" element={<Messages />} />
                          <Route path="/messages/people" element={<MessageDirectory />} />
                          <Route path="/documents" element={<Documents />} />
                          <Route path="/camera-capture" element={<CameraCapture />} />
                          <Route path="/calendar" element={<Calendar />} />
                          <Route path="/notifications" element={<Notifications />} />
                          <Route path="/payments" element={<Payments />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/legal-review" element={<ProtectedRoute allowedRoles={['lawyer']}><LegalReview /></ProtectedRoute>} />
                          <Route path="/due-diligence" element={<ProtectedRoute allowedRoles={['lawyer']}><DueDiligence /></ProtectedRoute>} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/help" element={<Help />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      </ErrorBoundary>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </ErrorBoundary>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
